import { GlobalExclusionsView } from '../view/GlobalExclusionsView';
import { templateGlobalExclusions } from '../view/template';
import { GlobalExclusionsRepository } from '../repository/GlobalExclusionsRepository';
import { BaseWebComponentController, WebComponentEvent } from '../../../framework/BaseWebComponentController';
import { GlobalExclusionsWebComponent } from '..';
import { DataGridItemList, GlobalExclusionsEvents, ToastActionConfig } from '../types';
import { GridEvents } from '../../../grid/types/common';
import { GzDialogWebComponent } from '../../../ui/gzDialog';
import { GzToastNotificationWebComponent, showErrorToast, showSuccessToast } from '../../../ui/gzToastNotification';
import { ToastSeverity } from '../../../ui/gzToastNotification/type';
import { State } from '../../../grid/uiElements/inlineRowActions/types';
import { ColumnKeys } from '../../exclusionItemGrid/types';
import { RequiredValidator } from '../../../ui/gzForm/validators/required.validator';
import { ExclusionItemWildcardValidator, ExclusionValidator } from '../../../ui/gzForm/validators/exclusion.validator';
import { WEB_COMPONENT_CONFIGURATION_PROFILES, WEB_COMPONENT_EXCLUSION_LIST_SELECTOR } from '../../../webcomponents.constants';
import { ExclusionListSelectorWebComponent } from '../../exclusionListSelector';
import { EnumListEvents } from '../../../ui/gzEnumList/types';
import { GzDialog } from '../../../ui/gzDialog/component/GzDialog';
import { Rights } from '@nirvana/shared/constants';
import { translateFn } from '../../../utils/translator.helper';
import { InlineRowActionsWebComponent } from '../../../grid/uiElements/inlineRowActions';

export class GlobalExclusionsController extends BaseWebComponentController {
  public component: GlobalExclusionsWebComponent;
  public dataRepository: GlobalExclusionsRepository;
  public view: GlobalExclusionsView;
  public viewConstructor: typeof GlobalExclusionsView;
  public dataRepositoryConstructor: typeof GlobalExclusionsRepository;

  private selectedItemsData: Array<any> = [];

  constructor(component: GlobalExclusionsWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: GlobalExclusionsView,
      viewTemplate: templateGlobalExclusions,
      dataRepositoryConstructor: GlobalExclusionsRepository,
    });
  }

  private static showErrorToast(errorMessage: string, actionConfig?: ToastActionConfig) {
    let config = {};

    if (actionConfig) {
      config = {
        hasAction: true,
        ...actionConfig,
      };
    }

    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: errorMessage,
      severity: ToastSeverity.ERROR,
      ...config,
    });
  }

  private static showSuccessToast(successMessage: string) {
    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: successMessage,
      severity: ToastSeverity.SUCCESS,
    });
  }

  computeEventListeners() {
    return [
      {
        element: this.view.dom.$gridView,
        event: GlobalExclusionsEvents.DELETE_EXCLUSION_ITEM,
        callback: this.onDeleteExclusionItem,
      },
      {
        element: this.view.dom.$gridView,
        event: GlobalExclusionsEvents.ASSIGN_EXCLUSION_ITEM,
        callback: this.onAssignExclusionItem,
      },
      {
        element: this.view.dom.$gridView,
        event: GridEvents.EDIT_ROW_ENABLED,
        callback: this.setValidatorsOnEdit,
      },
      {
        element: this.view.dom.$gridView,
        event: GridEvents.EDIT_ROW_SAVE,
        callback: this.onEditRowSave,
      },
      {
        element: this.view.dom.$addExclusions,
        event: 'click',
        callback: this.addExclusions,
      },
      {
        element: this.view.dom.$exportSelection,
        event: 'click',
        callback: this.exportExclusions,
      },
      {
        element: this.view.dom.$deleteExclusions,
        event: 'click',
        callback: this.triggerMultipleDelete,
      },
      {
        element: this.view.dom.$gridView,
        event: GridEvents.SELECT_ROWS,
        callback: this.selectedItems,
      },
      {
        element: this.view.dom.$gridView,
        event: GridEvents.EXPORT_CSV,
        callback: this.exportCsv,
      },
      {
        element: this.view.dom.$assignToList,
        event: 'click',
        callback: this.onAssignToList,
      },
    ];
  }

  private setValidatorsOnEdit(event: WebComponentEvent) {
    const { rowData } = event.detail;

    // Set the validators on edited row according to the object type
    this.view.dom.$gridView?.controller?.setValidatorsOnRowAndColumn(rowData.index, ColumnKeys.EXCLUDED_ITEMS, [
        RequiredValidator(''),
        ExclusionValidator(rowData.item.objectType, ''),
        ExclusionItemWildcardValidator({
        type: rowData.item.objectType,
        path: rowData.item.excludedItems,
        modules: rowData.item.modules,
      }, translateFn('You cannot use wildcards')),
    ]);
  }

  private async onEditRowSave(event: WebComponentEvent) {
    const { excludedItems: newItem, remarks: newRemarks, modules: newModules } = event.detail.newData.item;
    const { excludedItems: oldItem, remarks: oldRemarks, modules: oldModules } = event.detail.rowData.item;
    const currentRowIndex = event.detail.rowData.index;

    const inlineMenu = this.view.dom?.$gridView?.controller?.view.getRowInlineMenu(currentRowIndex + 2) as InlineRowActionsWebComponent;

    if (newItem === oldItem && newRemarks === oldRemarks && newModules === oldModules) {
      // do not send an update request with same data
      inlineMenu.state = State.SUCCESS;

      setTimeout(() => {
        inlineMenu.state = null;
      }, 5000);

      return;
    }

    const records = [
      {
        _id: event.detail.rowData.item.id,
        description: newRemarks,
        companyId: Application.Main.getActiveCompany().id,
        specifics: {
          type: event.detail.rowData.item.objectType,
          path: newItem,
          modules: newModules,
        },
        listIds: event.detail.rowData.item.inLists.map(list => list.id),
      },
    ];

    this.view.dom.$gridView.setRowData(currentRowIndex, event.detail.newData.item);

    try {
      inlineMenu.state = State.LOADING;
      inlineMenu.disabled = true;

      await this.dataRepository.updateRecords(records);

      inlineMenu.disabled = false;
      inlineMenu.state = State.SUCCESS;

      setTimeout(() => {
        inlineMenu.state = null;
      }, 5000);
    } catch (e) {
      inlineMenu.disabled = false;
      inlineMenu.state = State.ERROR;

      const actionConfig: ToastActionConfig = {
        actionButtonText: translateFn('GO TO EXCLUSION'),
        actionCallback: () => {
          this.view.dom.$gridView.controller?.view.dom.$grid.scrollToIndex(currentRowIndex);
        },
      };

      GlobalExclusionsController.showErrorToast(translateFn('Failed to edit exclusion due to an internal service error.'), actionConfig);
    }
  }

  addExclusions(ev) {
    this.component.dispatchEvent(
      new CustomEvent(GlobalExclusionsEvents.ADD_EXCLUSIONS, {
        detail: {},
        bubbles: true,
      })
    );
  }

  exportExclusions() {
    if (this.selectedItemsData.length === 0) {
      return;
    }

    this.view.showExportExclusionsWindow(this.selectedItemsData);
  }

  private async exportCsv() {
    const url = this.dependencies.getExportToCsvUrl(
      this.view.dom.$gridView.extraParams,
      this.view.dom.$gridView.filters
    );

    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'exclusions.csv');
    document.body.appendChild(link);

    link.click();
    document.body.removeChild(link);
  }

  selectedItems(items: WebComponentEvent) {
    // global selected items
    this.selectedItemsData = items.detail.selectedItemsData;

    const disabled = !Application.Main.hasAllRights([Rights.MANAGE_POLICIES_WRITE]);

    // if no selection, disable buttons
    this.view.dom.$exportSelection.disabled = this.selectedItemsData.length === 0;
    this.view.dom.$deleteExclusions.disabled = disabled || this.selectedItemsData.length === 0;
    this.view.dom.$assignToList.disabled = disabled || this.selectedItemsData.length === 0;
    this.view.dom.$addExclusions.disabled = disabled;
  }

  private async triggerMultipleDelete() {
    if (this.selectedItemsData.length === 0) {
      return;
    }

    if (this.selectedItemsData.length === 1) {
      const { id, inLists } = this.selectedItemsData[0];
      this.showSingleDeleteConfirmationDialog(id, inLists, this.onConfirmSingleDeleteExclusionItem.bind(this));
      return;
    }

    const { affectedLists, selectedRecords } = this.prepareSelectedRecordsForDeleteRequest();

    this.showMultipleDeleteConfirmationDialog(
      affectedLists,
      selectedRecords,
      this.onConfirmMultipleDeleteExclusionItems.bind(this)
    );
  }

  private prepareSelectedRecordsForDeleteRequest() {
    let affectedLists: DataGridItemList[] = [];

    // selected records for backend sending
    const selectedRecords = this.selectedItemsData.map(item => {
      if (item.inLists.length > 0) {
        // save the lists which contain this exclusion
        affectedLists.push(...item.inLists);
      }

      return {
        _id: item.id,
        listIds: item.inLists.map(list => list.id),
        companyId: Application.Main.getActiveCompany().id,
      };
    });

    // remove duplicated lists by looking at the list id
    affectedLists = affectedLists.filter((list, index) => {
      return index === affectedLists.findIndex(l => l.id === list.id);
    });

    return { affectedLists, selectedRecords };
  }

  private showMultipleDeleteConfirmationDialog(affectedLists, selectedRecords, confirmationCallback) {
    const windowTitle = translateFn('Delete exclusions?');

    const warningText = translateFn('Deleting the selected exclusions will remove them from any lists and policies that they are currently assigned to.');

    const windowBodyHtml = this.view.getDeleteConfirmationDialogBodyHtml(
      warningText,
      affectedLists,
      this.selectedItemsData
    );

    const windowFooterHtml = this.view.getDeleteConfirmationDialogFooterHtml();

    const dialogDetail = {
      selectedRecords,
    };

    GzDialogWebComponent.computeCustomDialog(
      windowBodyHtml,
      windowFooterHtml,
      windowTitle,
      null,
      confirmationCallback,
      dialogDetail
    );
  }

  private showSingleDeleteConfirmationDialog(exclusionId, inLists, confirmCallback) {
    const windowTitle = translateFn('Delete exclusion?');

    const warningText = translateFn('Deleting the selected exclusion will remove it from any lists and policies that it is currently assigned to.');

    const windowBodyHtml = this.view.getDeleteConfirmationDialogBodyHtml(warningText, inLists, []);

    const windowFooterHtml = this.view.getDeleteConfirmationDialogFooterHtml();

    // extract list ids for lists with defined name
    const listIds = inLists.filter(list => list['name']).map(list => list['id']);

    const dialogDetail = {
      id: exclusionId,
      listIds: listIds,
    };

    GzDialogWebComponent.computeCustomDialog(
      windowBodyHtml,
      windowFooterHtml,
      windowTitle,
      null,
      confirmCallback,
      dialogDetail
    );
  }

  private async onConfirmMultipleDeleteExclusionItems(event) {
    const unknownErrorMessage = translateFn('Exclusion items not deleted');
    let removeError = null;

    try {
      // call remove records
      const removeResponse = await this.dataRepository.removeRecords(event.detail.selectedRecords);

      if (!removeResponse || !removeResponse.success) {
        // unknown error
        removeError = unknownErrorMessage;
      }
    } catch (error) {
      removeError = error.message || unknownErrorMessage;
    }

    if (removeError) {
      // show error toast
      GlobalExclusionsController.showErrorToast(removeError);
      return;
    }

    // show success toast
    const successMessage = translateFn('Exclusion items deleted successfully');
    GlobalExclusionsController.showSuccessToast(successMessage);

    // reload grid data
    this.view.dom.$gridView.propertiesSet = true;
  }

  private async onConfirmSingleDeleteExclusionItem(event) {
    const record = {
      _id: event.detail.id,
      listIds: event.detail.listIds,
      companyId: Application.Main.getActiveCompany().id,
    };

    const unknownErrorMessage = translateFn('Exclusion item not deleted');
    let removeError = null;

    try {
      const response = await this.dataRepository.removeRecords([record]);

      if (!response || !response.success) {
        removeError = unknownErrorMessage;
      }
    } catch (error) {
      removeError = error.message || unknownErrorMessage;
    }

    if (removeError) {
      GlobalExclusionsController.showErrorToast(removeError);
      return;
    }

    const successMessage = translateFn('Exclusion item deleted successfully');
    GlobalExclusionsController.showSuccessToast(successMessage);

    this.component.dispatchEvent(
      new CustomEvent(GlobalExclusionsEvents.CONFIRM_DELETE_EXCLUSION_ITEM, {
        detail: {},
        bubbles: true,
        composed: true,
      })
    );
  }

  onDeleteExclusionItem(event: WebComponentEvent) {
    const { id, inLists } = event.detail.rowData.item;
    this.showSingleDeleteConfirmationDialog(id, inLists, this.onConfirmSingleDeleteExclusionItem.bind(this));
  }

  private async onConfirmAssignExclusionItem(params) {
    const companyId = Application.Main.getActiveCompany().id;
    const itemId = params['id'] ?? null;
    const response = await this.dataRepository.assignItemToMultipleLists(companyId, itemId, params['listsIds'], true);
    if (!response || !response.success) {
      showErrorToast(translateFn("An unknown error has occurred."));
      return;
    }

    await this.render();
    showSuccessToast(translateFn("Exclusion assigned to lists successfully."));
  }

  async onAssignExclusionItem(event: WebComponentEvent) {
    const { id, inLists } = event.detail.rowData.item;
    const assignedExclusionsListsIds = inLists.map(item => {
      return item.id;
    });
    this.view.showExclusionItemAssignToList(id, assignedExclusionsListsIds, this.onConfirmAssignExclusionItem.bind(this));
  }

  async getDataForView(): Promise<any> {
    return {
      activeExclusionList: this.component.activeExclusionList,
    };
  }

  private onAssignToList() {
    const exclusionIds: string[] = [];
    this.selectedItemsData.forEach(exclusion => {
      return exclusionIds.push(exclusion.id);
    });
    this.showAssignExclusionsToList(exclusionIds);
  }

  public showAssignExclusionsToList(exclusionIds: string[]) {
    const windowBodyHtml = document.createElement('div');
    windowBodyHtml.classList.add('px-5', 'py-3');
    windowBodyHtml.innerHTML = `
    <gz-title fontWeight="400" fontSize="14">${translateFn('Choose the lists where to include the selected exclusion rule.')}</gz-title><br>
    <div><gz-exclusion-list-selector></gz-exclusion-list-selector></div><br><br>
    <gz-title class="title" fontweight="400" fontsize="18">${translateFn('Lists Preview')}</gz-title>`;

    const tableContainer = document.createElement('div');
    const table = this.view.createImpactTable();
    let selectedExclusionsList = [];

    const selector = windowBodyHtml.querySelector(
      WEB_COMPONENT_EXCLUSION_LIST_SELECTOR
    ) as ExclusionListSelectorWebComponent;
    selector.injectDependencies(this.dependencies);
    selector.addEventListener(EnumListEvents.SELECTION_CHANGE, (e: CustomEvent) => {
      const dataExclusionList = e?.currentTarget?.['exclusionList'] || [];
      selectedExclusionsList = e.detail.selectedValues.map(exclusionIndex => {
        return dataExclusionList[exclusionIndex]['value'];
      });

      if (selectedExclusionsList.length > 0) {
        tableContainer.appendChild(table);
        windowBodyHtml.appendChild(tableContainer);
        this.view.updateImpactTable(table, selectedExclusionsList, dataExclusionList);
      }
    });
    selector.start().then().catch(console.error);

    const windowFooterHtml = document.createElement('div');
    windowFooterHtml.innerHTML = `<gz-button id='confirm' type='full' class='mr-2'>${translateFn('ASSIGN')}</gz-button>
      <gz-button id='cancel' type='outline' color='secondary'>${translateFn('CANCEL')}</gz-button>`;

    const windowTitle = translateFn('Assign exclusions to lists');
    const windowConfigs: GzDialog = {
      headline: windowTitle,
      bodyContent: windowBodyHtml,
      footerContent: windowFooterHtml,
      isOpen: true,
      isWindow: true,
    };

    const configurationProfiles = document.body.querySelector(WEB_COMPONENT_CONFIGURATION_PROFILES)
      ?.parentElement as HTMLElement;
    const dialog = GzDialogWebComponent.buildDialogElement(windowConfigs, configurationProfiles);
    const footerConfirmBtn = windowFooterHtml.querySelector('#confirm');
    footerConfirmBtn?.addEventListener('click', async () => {
      await this.onConfirmAssignExclusionsToList(dialog, exclusionIds, selectedExclusionsList);
    });

    const footerCancerBtn = windowFooterHtml.querySelector('#cancel');
    footerCancerBtn?.addEventListener('click', () => {
      dialog.close();
    });
  }

  async onConfirmAssignExclusionsToList(dialog: GzDialogWebComponent, exclusionIds: string[], exclusionsListIds: string[]) {
    if (!exclusionIds.length || !exclusionsListIds.length) {
      dialog.close();
      return;
    }

    const companyId = Application.Main.getActiveCompany().id;
    const response = await this.dataRepository.assignMultipleItemsToMultipleLists(companyId, exclusionIds, exclusionsListIds);
    if (!response || !response.success) {
      dialog.close();
      showErrorToast(translateFn("An unknown error has occurred."));
      return;
    }

    this.component.dispatchEvent(new CustomEvent(GlobalExclusionsEvents.ASSIGN_ITEMS_TO_LISTS, {detail: {}, bubbles: true}));
    dialog.close();
    showSuccessToast(translateFn("Exclusions assigned to lists successfully."));
  }
}
